import React, { useState } from 'react'
import Icon from 'components/elements/Icon'
import { useCookies } from 'react-cookie'
import ReactGA from 'react-ga4'

import { DeckCardInfoType } from 'types/search'
import { DEFAULT_DECK_CARD_IMAGE } from 'types/deck'

import BookmarkService from 'services/bookmark.service'
import ToastService from 'services/toast.service'

import Link from 'components/elements/Link'
import Budget from 'components/packages/Budget'
import IntendedFormats from 'components/packages/IntendedFormats'
import ColorBar from 'components/deckPage/charts/ColorBar'

import { encodeUsernameRoute } from 'utils/encoding'
import { generateDeckUrl } from 'utils/deckSeo'

import styles from './packageListItem.module.scss'

type Props = {
  package: DeckCardInfoType
  onAddPackage?: (deckId: number) => void
  onViewPackageDetails?: (deckId: number | null) => void
  openInNewTab?: boolean
  className?: string
}

const PackageListItem = ({
  package: deck,
  onAddPackage,
  onViewPackageDetails,
  openInNewTab,
  className = '',
}: Props) => {
  const [bookmarked, setBookmarked] = useState(deck?.bookmarked ?? false)
  const [{ tbId: userId }] = useCookies(['tbId'])

  const handleToggleBookmark = () => {
    if (bookmarked)
      return BookmarkService.remove(deck.id)
        .then(() => ToastService.create('Bookmark removed', 'Bookmark service', 'success'))
        .then(() => setBookmarked(false))
        .catch(() => ToastService.create('Unable to delete bookmark', 'Bookmark service'))

    return BookmarkService.create(deck.id)
      .then(() => ToastService.create('Card package bookmarked', 'Bookmark service', 'success'))
      .then(() => setBookmarked(true))
      .catch(() => ToastService.create('Unable to create bookmark', 'Bookmark service'))
  }

  return (
    <div className={`${styles.package} ${className}`}>
      <div className={styles.left}>
        <div className={styles.previewImage}>
          <img src={deck.customFeatured || deck.featured || DEFAULT_DECK_CARD_IMAGE} />
          <div className={styles.imageBlur} />
        </div>

        <div className={styles.info}>
          <Link
            href={generateDeckUrl(deck.id, deck.name)}
            className={styles.title}
            target={openInNewTab ? '_blank' : undefined}>
            {deck.name}
          </Link>
          <div className={styles.keywords}>
            <label className={styles.label}>Keywords:</label> {deck.cardPackage?.keywords}
          </div>
          <div className={styles.keywords}>
            <span className={styles.label}>Budget:</span> <Budget budget={deck.cardPackage?.budget} />
          </div>
          <div className={styles.keywords}>
            <span className={styles.label}>Intended Formats:</span>{' '}
            <IntendedFormats intendedFormats={deck.cardPackage?.intendedFormats} />
          </div>
          <div className={styles.keywords}>
            <span className={styles.label}>Times Imported:</span> {deck.cardPackage?.importCount}
          </div>
          <div>
            <span className={styles.label}>Created By:</span>{' '}
            <Link
              href={`/u/${encodeUsernameRoute(deck?.owner?.username || '')}`}
              target={openInNewTab ? '_blank' : undefined}>
              {deck.owner?.username}
            </Link>
          </div>
          <ColorBar colors={deck.colors} includeManaSymbols className={styles.colorBar} />
        </div>
      </div>

      {onAddPackage && onViewPackageDetails && (
        <div className={styles.controls}>
          <button title="Add cards to your deck" onClick={() => onAddPackage(deck.id)}>
            <Icon name="add" />
            Add to deck
          </button>

          <button title="Preview package contents" onClick={() => onViewPackageDetails(deck.id)}>
            <Icon name="arrow right" />
            Preview cards
          </button>

          <button disabled={!userId} title="Bookmark card package" onClick={() => handleToggleBookmark()}>
            <Icon
              name={bookmarked ? 'bookmark' : 'bookmark outline'}
              className={bookmarked ? styles.bookmarked : undefined}
            />
            Bookmark
          </button>
        </div>
      )}
    </div>
  )
}

export default PackageListItem
